import classNames from 'classnames'

import { useManagementGetCompanies } from '@apps-common/apis/management/company'
import { useManagementGetProductNetworkTypes } from '@apps-common/apis/management/product'
import { ButtonRoundSm, CheckboxInputSM, ControlledInputDebounced, SelectInputSM } from '@apps-common/components/elements'
import { ModalBody, ModalHeader, ModalHeaderContainer } from '@apps-common/components/modals/modal'
// import { COMPANY_TYPE_BURO_NAME, COMPANY_TYPE_MONITORING_CENTRE_NAME } from '@apps-common/config/company'
import { commonIcons } from '@apps-common/config/icons'
import {
  INPUT_CLASSES,
  INPUT_CONTAINER_CLASSES,
  INPUT_DATEPICKER_CLASSES_SM,
  INPUT_DATEPICKER_CONTAINER_SM
} from '@apps-common/config/input-styles'
import { ModalDismiss } from '@apps-common/config/modal'
import { UNIT_ORDER_VALUES, UNIT_STATUS_ACTIVE, UNIT_STATUS_CANCELLED, UNIT_STATUS_VALUES } from '@apps-common/config/unit-filter'
import {
  getManagementUnitsFilterDefaultEndDateStr,
  getManagementUnitsFilterDefaultStartDateStr,
  useManagementUnitsFilterStore
} from '@apps-common/stores/useManagementUnitsFilterStore'
import { IManagementUnitFilterDefaults } from '@apps-common/types/management/unit'

import { ControlledDatepickerInputSM } from '../elements/form/ControlledDatepickerInputSM'
import { FilterModalContainer } from './filter/FilterModalContainer'

export interface IManagementUnitsFilterModal {
  defaults?: IManagementUnitFilterDefaults
}

export const ManagementUnitsFilterModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IManagementUnitsFilterModal
}) => {
  const state = useManagementUnitsFilterStore()
  const filterDefaults = componentProps?.defaults?.filter

  const {
    data: companies,
    isLoading: companiesIsLoading,
    isError: companiesIsError
  } = useManagementGetCompanies({
    config: {
      gcTime: 60000,
      staleTime: 60000
    },
    name: ''
  })

  const {
    data: networkTypes,
    isLoading: networkTypesIsLoading,
    isError: networkTypesIsError
  } = useManagementGetProductNetworkTypes({
    config: {
      gcTime: 60000,
      staleTime: 60000
    }
  })

  const loading = companiesIsLoading || companiesIsError || networkTypesIsLoading || networkTypesIsError

  return (
    <FilterModalContainer onDismiss={onDismiss} classes='sm:!max-w-[500px] sm:!h-auto' useFullHeight>
      <ModalHeaderContainer classes='!px-[1rem]'>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle='Management devices' title='Filter' subtitleClasses='capitalize' />
          <div className='flex items-center gap-[10px]'>
            <ButtonRoundSm
              type='button'
              className='action-brand'
              callback={() => {
                state.clear()
                onDismiss(null, ModalDismiss.Confirm)
              }}
            >
              <commonIcons.reload />
            </ButtonRoundSm>
            <ButtonRoundSm
              type='button'
              className='action-brand-solid'
              callback={() => {
                onDismiss(null, ModalDismiss.Cancel)
              }}
            >
              <commonIcons.close />
            </ButtonRoundSm>
          </div>
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='!pt-[100px] !pb-[30px] sm:!pb-[20px] !px-[1rem]'>
        <div className='gap-[10px] grid'>
          <div className='grid sm:grid-cols-2 sm:gap-y-2 sm:gap-x-3'>
            {/* SITE NAME */}
            {!filterDefaults?.hideSite && (
              <ControlledInputDebounced
                type='text'
                placeholder='Site name'
                label='Site name'
                id='unit-management-filter-site-name'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                showLabel
                hideErrors
                value={state.site}
                disabled={filterDefaults?.disableSite || loading}
                onChange={(e) => state.setSite(e?.target?.value || '')}
              />
            )}
            {/* ACCOUNT NO */}
            {!filterDefaults?.hideAccountNo && (
              <ControlledInputDebounced
                type='text'
                placeholder='Account number'
                label='Account number'
                id='unit-management-filter-account-number'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                showLabel
                hideErrors
                value={state.accountNo}
                disabled={filterDefaults?.disableAccountNo || loading}
                onChange={(e) => state.setAccountNo(e?.target?.value || '')}
              />
            )}
            {/* IMEI */}
            {!filterDefaults?.hideImei && (
              <ControlledInputDebounced
                type='text'
                placeholder='IMEI number'
                label='IMEI number'
                id='unit-management-filter-imei-number'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                showLabel
                hideErrors
                value={state.imei}
                disabled={filterDefaults?.disableImei || loading}
                onChange={(e) => state.setImei(e?.target?.value || '')}
              />
            )}
            {/* SERIAL NO */}
            {!filterDefaults?.hideSerialNo && (
              <ControlledInputDebounced
                type='text'
                placeholder='Serial number'
                label='Serial number'
                id='unit-management-filter-serial-number'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                showLabel
                hideErrors
                value={state.serialNo}
                disabled={filterDefaults?.disableSerialNo || loading}
                onChange={(e) => state.setSerialNo(e?.target?.value || '')}
              />
            )}
            {/* MOBILE NO */}
            {!filterDefaults?.hideMobileNo && (
              <ControlledInputDebounced
                type='text'
                placeholder='Mobile number'
                label='Mobile number'
                id='unit-management-filter-mobile-number'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                showLabel
                hideErrors
                value={state.mobileNo}
                disabled={filterDefaults?.disableMobileNo || loading}
                onChange={(e) => state.setMobileNo(e?.target?.value || '')}
              />
            )}
            {/* IP ADDRESS */}
            {!filterDefaults?.hideIpAddress && (
              <ControlledInputDebounced
                type='text'
                placeholder='IP address'
                label='IP Address'
                id='unit-management-filter-ip-address'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                showLabel
                hideErrors
                value={state.ipAddress}
                disabled={filterDefaults?.disableIpAddress || loading}
                onChange={(e) => state.setIpAddress(e?.target?.value || '')}
              />
            )}
            {/* SIM NUMBER */}
            {!filterDefaults?.hideSimNo && (
              <ControlledInputDebounced
                type='text'
                placeholder='Sim number'
                label='Sim number'
                id='unit-management-filter-sim-number'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                showLabel
                hideErrors
                value={state.simNo}
                disabled={filterDefaults?.disableSimNo || loading}
                onChange={(e) => state.setSimNo(e?.target?.value || '')}
              />
            )}
            {/* UNIT STATUS */}
            {!filterDefaults?.hideStatus && (
              <SelectInputSM
                label='Status'
                id='unit-management-filter-status'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                showLabel
                options={UNIT_STATUS_VALUES}
                disabled={filterDefaults?.disableStatus || loading}
                hideError
                registration={{ value: state.status, onChange: (e: any) => state.setStatus(e?.target?.value || '') }}
              />
            )}
            {/* MONITORING CENTRE */}
            {!filterDefaults?.hideMonitoringCentre && !state.pinnedUnits && (
              <SelectInputSM
                placeholder='Select Monitoring Centre'
                label='Monitoring Centre'
                id='unit-management-filter-monitoring-centre'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                showLabel
                options={[
                  { label: 'All Centres', value: '' },
                  ...(companies?.map((c) => ({ label: c.companyName, value: c.companyId })) || [])
                ]}
                disabled={filterDefaults?.disableMonitoringCentre || loading}
                hideError
                registration={{ value: state.mCentreId, onChange: (e: any) => state.setMCentreId(e?.target?.value || '') }}
              />
            )}
            {/* BUREAU */}
            {!filterDefaults?.hideBureau && !state.pinnedUnits && (
              <SelectInputSM
                placeholder='Select Bureau'
                label='Bureau'
                id='unit-management-filter-bureau'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                showLabel
                options={[
                  { label: 'All Bureaus', value: '' },
                  ...(companies?.map((c) => ({ label: c.companyName, value: c.companyId })) || [])
                ]}
                disabled={filterDefaults?.disableBureau || loading}
                hideError
                registration={{ value: state.companyId, onChange: (e: any) => state.setCompanyId(e?.target?.value || '') }}
              />
            )}
            {/* TECHNICIAN */}
            {!filterDefaults?.hideTech && !state.pinnedUnits && (
              <SelectInputSM
                placeholder='Select Tech'
                label='Technician'
                id='unit-management-filter-technician'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                showLabel
                options={[
                  { label: 'All Technicians', value: '' },
                  ...(companies?.map((c) => ({ label: c.companyName, value: c.companyId })) || [])
                ]}
                disabled={filterDefaults?.disableTech || loading}
                hideError
                registration={{ value: state.techCompanyId, onChange: (e: any) => state.setTechCompanyId(e?.target?.value || '') }}
              />
            )}
            {/* NETWORK TYPES */}
            {!filterDefaults?.hideNetworkType && (
              <SelectInputSM
                placeholder='Select Network'
                label='Network'
                id='unit-management-filter-network-type'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                showLabel
                options={[
                  { label: 'All Networks', value: '' },
                  ...(networkTypes?.map((nt) => ({ label: nt.networkType, value: nt.networkType })) || [])
                ]}
                disabled={filterDefaults?.disableNetworkType || loading}
                hideError
                registration={{ value: state.networkType, onChange: (e: any) => state.setNetworkType(e?.target?.value || '') }}
              />
            )}
            {/* START DATE */}
            <ControlledDatepickerInputSM
              label='Start date'
              id='unit-filter-start-date'
              name='startDate'
              placeholder='Start date'
              showLabel
              disabled={loading}
              className={INPUT_DATEPICKER_CLASSES_SM}
              containerClassName={INPUT_DATEPICKER_CONTAINER_SM}
              value={state.startDate || getManagementUnitsFilterDefaultStartDateStr()}
              onChange={(value) => state.setStartDate(value)}
              minDate={new Date(getManagementUnitsFilterDefaultStartDateStr())}
              maxDate={new Date(getManagementUnitsFilterDefaultEndDateStr())}
            />
            {/* END DATE */}
            <ControlledDatepickerInputSM
              label='End date'
              id='unit-filter-end-date'
              name='endDate'
              placeholder='End date'
              showLabel
              disabled={loading}
              className={INPUT_DATEPICKER_CLASSES_SM}
              containerClassName={INPUT_DATEPICKER_CONTAINER_SM}
              value={state.endDate || getManagementUnitsFilterDefaultEndDateStr()}
              onChange={(value) => state.setStartDate(value)}
              minDate={new Date(getManagementUnitsFilterDefaultStartDateStr())}
              maxDate={new Date(getManagementUnitsFilterDefaultEndDateStr())}
            />
          </div>

          {/* ORDER BY */}
          {!filterDefaults?.hideSortBy && (
            <SelectInputSM
              placeholder='sort by'
              label='Sort by'
              id='unit-filter-sort-by'
              className={INPUT_CLASSES}
              containerClassName={classNames(INPUT_CONTAINER_CLASSES, 'sm:col-span-2')}
              showLabel
              options={UNIT_ORDER_VALUES.filter((o) => !o.valueCheck || o.valueCheck === state.status)}
              disabled={filterDefaults?.disableSortBy || loading}
              registration={{ value: state.orderBy, onChange: (e: any) => state.setOrderBy(e?.target?.value || '') }}
            />
          )}
        </div>
        {!filterDefaults?.disableUnprocessedCancellations && state.status === UNIT_STATUS_CANCELLED.value && (
          <CheckboxInputSM
            label='Show un-processed'
            registration={{
              checked: state.unprocessedCancellations,
              onChange: (e: any) => state.setUnprocessedCancellations(e?.target?.checked || false)
            }}
            disabled={filterDefaults?.disableUnprocessedCancellations || loading}
          />
        )}
        {state.status === UNIT_STATUS_ACTIVE.value && !filterDefaults?.hidePinnedUnits && (
          <CheckboxInputSM
            label='Pinned'
            registration={{
              checked: state.pinnedUnits,
              onChange: (e: any) => {
                const value = e?.target?.checked

                if (value) {
                  state.setMCentreId('')
                  state.setCompanyId('')
                  state.setTechCompanyId('')
                }
                state.setPinnedUnits(value || false)
              }
            }}
            disabled={filterDefaults?.disablePinnedUnits || loading}
          />
        )}
        {state.status === UNIT_STATUS_ACTIVE.value && !filterDefaults?.hideHardwareToBeInvoiced && (
          <CheckboxInputSM
            label='Hardware requires invoicing'
            registration={{
              checked: state.hardwareToBeInvoiced,
              onChange: (e: any) => {
                const value = e?.target?.checked

                if (value) {
                  state.setMCentreId('')
                  state.setCompanyId('')
                  state.setTechCompanyId('')
                }
                state.setHardwareToBeInvoiced(value || false)
              }
            }}
            disabled={filterDefaults?.disabledHardwareToBeInvoiced || loading}
          />
        )}
      </ModalBody>
    </FilterModalContainer>
  )
}
